import {ApolloLink, Observable, Operation, ApolloClient, InMemoryCache, createHttpLink} from "@apollo/client";
import {onError} from "@apollo/client/link/error";
import {fromPromise} from "@apollo/client/link/utils";
import {LOCAL_STORAGE_KEYS} from "../common/constants";
import {getAppTokenHeaders} from "./appTokenHeaders";
import {RefreshTokenDocument} from "./generated/hooks";

const normalizeEndpoint = (value: string) =>
    value.replace("api.robinfood.dev", "api.dev.robinfood.dev");

const fallbackEndpoint =
    process.env.REACT_APP_ENV === "prod"
        ? "https://api.robinfood.app"
        : "https://api.dev.robinfood.dev";

const endpoint = normalizeEndpoint(
    process.env.REACT_APP_GRAPHQL_ENDPOINT || fallbackEndpoint
);

let isRefreshing = false;
let pendingRequests: Array<() => void> = [];
let isRedirectingToLogin = false;

const createRefreshClient = () => {
    return new ApolloClient({
        link: createHttpLink({
            uri: `${endpoint}/graphql`,
            headers: getAppTokenHeaders(),
        }),
        cache: new InMemoryCache(),
    });
};

const setAuthorizationHeader = (operation: Operation, token: string | null) => {
    operation.setContext(({headers = {}}: { headers?: Record<string, string> }) => ({
        headers: {
            ...headers,
            ...getAppTokenHeaders(),
            authorization: token ? `Bearer ${token}` : "",
        },
    }));
};

const refreshAccessToken = async (): Promise<string | null> => {
    try {
        const refreshToken = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
        if (!refreshToken) {
            return null;
        }
        const refreshClient = createRefreshClient();
        const result = await refreshClient.mutate({
            mutation: RefreshTokenDocument,
            variables: {
                refreshToken,
            },
        });
        if (!result.data?.refreshToken) {
            return null;
        }
        const {accessToken, refreshToken: newRefreshToken} = result.data.refreshToken;
        localStorage.setItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN, accessToken);
        if (newRefreshToken) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
        }
        return accessToken;
    } catch (error) {
        return null;
    }
};

const handleLogout = () => {
    // Don't redirect if we're on a public route (invite, login, etc.)
    const currentPath = window.location.pathname;
    const isPublicRoute = currentPath.startsWith("/invite/") || 
                         currentPath.startsWith("/login") ||
                         currentPath.startsWith("/forgot-password") ||
                         currentPath.startsWith("/reset-password/");
    
    if (isPublicRoute) {
        // Just clear tokens, don't redirect
        localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
        isRedirectingToLogin = false;
        return;
    }

    if (isRedirectingToLogin) {
        return;
    }
    isRedirectingToLogin = true;
    localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
    window.location.replace("/login");
};

const resolvePendingRequests = () => {
    pendingRequests.forEach((callback) => callback());
    pendingRequests = [];
};

export const errorLink = onError(({graphQLErrors, operation, forward}) => {
    if (graphQLErrors) {
        // Check if we're on a public route - don't handle auth errors for public routes
        const currentPath = window.location.pathname;
        const isPublicRoute = currentPath.startsWith("/invite/") || 
                             currentPath.startsWith("/login") ||
                             currentPath.startsWith("/forgot-password") ||
                             currentPath.startsWith("/reset-password/");
        
        // First, check if ANY error is an auth error
        const hasAuthError = graphQLErrors.some(err =>
            err.extensions?.code === "UNAUTHENTICATED" ||
            err.extensions?.code === "UNAUTHORIZED_TOKEN_VALIDATION" ||
            err.extensions?.code === "FORBIDDEN" ||
            err.message?.includes("Unauthorized") ||
            err.message?.includes("jwt expired") ||
            err.message?.includes("invalid token") ||
            err.message?.includes("User validation failed") ||
            err.message?.includes("Forbidden resource")
        );
        if (hasAuthError) {
            // Don't handle auth errors for public routes or login/refresh/invitation operations
            if (
                isPublicRoute ||
                operation.operationName === "PortalLogin" ||
                operation.operationName === "RefreshToken" ||
                operation.operationName === "ValidateInvitation" ||
                operation.operationName === "AcceptInvitation"
            ) {
                return;
            }
            if (isRefreshing) {
                return fromPromise(
                    new Promise<void>((resolve) => {
                        pendingRequests.push(() => resolve());
                    })
                ).flatMap(() => {
                    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
                    setAuthorizationHeader(operation, token);
                    return forward(operation);
                });
            }
            isRefreshing = true;
            return fromPromise(
                refreshAccessToken()
                    .then((newToken) => {
                        if (!newToken) {
                            handleLogout();
                            return null;
                        }
                        setAuthorizationHeader(operation, newToken);
                        resolvePendingRequests();
                        return newToken;
                    })
                    .catch((error) => {
                        handleLogout();
                        return null;
                    })
                    .finally(() => {
                        isRefreshing = false;
                    })
            ).flatMap((token) => {
                if (!token) {
                    return Observable.of();
                }
                return forward(operation);
            });
        }
    }
});

export const authLink = new ApolloLink((operation, forward) => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
    setAuthorizationHeader(operation, token);
    return forward(operation);
});
